// A lazily loaded route chunk can 404 during a frontend rollout: app.html
// comes from a new pod and names a hashed chunk that an old pod behind the
// same Service does not have. Reloading fetches app.html again and usually
// lands on a consistent pod. The counter in sessionStorage bounds the
// reloads; without working storage we never reload (no way to stop a loop).

export interface ReloadDeps {
  storage: Storage;
  reload: () => void;
  key: string;
  maxReloads: number;
}

function readCount(storage: Storage, key: string): number | null {
  try {
    return Number(storage.getItem(key) ?? "0") || 0;
  } catch {
    return null;
  }
}

function forget(storage: Storage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    // Storage unavailable: nothing to clear.
  }
}

export async function importWithReload<T>(load: () => Promise<T>, deps: ReloadDeps): Promise<T> {
  try {
    const mod = await load();
    forget(deps.storage, deps.key);
    return mod;
  } catch (err) {
    const count = readCount(deps.storage, deps.key);
    if (count === null || count >= deps.maxReloads) {
      forget(deps.storage, deps.key);
      throw err;
    }
    try {
      deps.storage.setItem(deps.key, String(count + 1));
    } catch {
      throw err;
    }
    deps.reload();
    // The page is reloading; never settle so React keeps the Suspense fallback.
    return new Promise<T>(() => {});
  }
}

export function browserReloadDeps(name: string): ReloadDeps {
  return {
    storage: window.sessionStorage,
    reload: () => window.location.reload(),
    key: `carmen-chunk-reload:${name}`,
    maxReloads: 2,
  };
}
